import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { useState } from 'react';
import { validate as validateUUID } from 'uuid';

import CollectionList from '@/components/AppointmentScheduler/CollectionList.tsx';
import SlotAdjustmentModal from '@/components/AppointmentScheduler/SlotAdjustmentModal.tsx';
import CircularLoader from '@/components/CircularLoader.tsx';
import { ChainBookingSlotTuner } from '@/components/chain-booking/chain-booking-slot-tuner.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { Appointment, AvailabilityStatus, CollectionStep, DailyAppointment } from '@/models/appointment/Appointment.ts';
import {
    AppFormInitialState,
    AppointmentBookingFormValues,
} from '@/models/appointment/AppointmentBookingFormValues.ts';
import { ChainBookingStep } from '@/models/appointment/AppointmentChainConfig.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { selectCurrentForm, useBookingStore } from '@/stores/bookingStore.ts';
import { useConfigStore } from '@/stores/configStore.ts';

import { formToAppointment } from '@/utils/appointmentUtils.ts';
import { mergeBookedItemsByOrder } from '@/utils/collection-utils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { generateInitialAppointFormState } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { openPatientModal } from '@/utils/modalUtils.tsx';
import { convertGenericPatientToDailyAppointmentPatient } from '@/utils/patientUtils.ts';

import CollapsedAppointment from './CollapsedAppointment.tsx';
import { AppointmentForm } from './appointment-form.tsx';
import { BookingActionsBar } from './booking-actions-bar.tsx';
import { BookingTaskTiles } from './booking-task-tiles.tsx';
import { CollectionSummaryCard } from './collection-summary-card.tsx';

const EXPAND_COLLAPSE_ANIMATION = {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.25, ease: 'easeInOut' as const },
    style: { overflow: 'hidden' as const },
};

export const AppointmentScheduler = () => {
    const {
        current_appointment_or_draft_id: currentAppointmentOrDraftId,
        appointments_form_draft: appointmentsFormDraft,
        initial_form_values: initialFormValues,
        is_last_form_valid: isLastFormValid,
        setIsLastFormValid,
        isSeriesMode,
        is_chain_mode: isChainMode,
        chain_booking_step: chainBookingStep,
        collectionStep,
        collection_occurrences: collectionOccurrences,
        is_booking_loading: isBookingLoading,
        isCollectionSubmitting,
        collection_bookings: collectionBookings,

        getCurrentBatch,
        editing_appointment: editingAppointment,
        current_patient: currentPatient,
    } = useBookingStore((state) => state);

    const [editingId, setEditingId] = useState<string | undefined>();
    const editingIndex = editingId === undefined ? -1 : collectionOccurrences.findIndex((o) => o.id === editingId);
    const editingOccurrence = editingIndex >= 0 ? collectionOccurrences[editingIndex] : undefined;
    const currentForm = useBookingStore(selectCurrentForm);
    const currentBatch = getCurrentBatch();
    const { data: formOptionData, is_loading: isLoading } = useEnrichedPractitionerDataForSpecificInstance(
        currentPatient?.instance_id ?? ''
    );
    const selectedLocation = useConfigStore((state) => state.selected_location);

    const hasBookingContext = collectionBookings.length > 0 || (currentBatch?.enriched_appointments.length ?? 0) > 0;

    const orderedBookedItems = mergeBookedItemsByOrder(collectionBookings, currentBatch?.enriched_appointments);

    const getFormInitialState = (): AppFormInitialState | undefined => {
        if (!formOptionData) {
            return undefined;
        }

        const currentDraft = appointmentsFormDraft.find((d) => d.id === currentAppointmentOrDraftId);
        const formValues = currentDraft ?? initialFormValues ?? {};

        return generateInitialAppointFormState({
            pvsProvider: formOptionData.pvs_provider,
            formOptionData: formOptionData,
            locationKey: selectedLocation?.key,
            prefilledValues: formValues,
            currentPatient: currentPatient,
        });
    };

    const formInitialState = getFormInitialState();

    const setCurrentFormData = (values: AppointmentBookingFormValues): void => {
        const currentId = useBookingStore.getState().current_appointment_or_draft_id;
        if (!currentId) return;

        useBookingStore.setState((state) => ({
            appointments_form_draft: state.appointments_form_draft.map((apt) =>
                apt.id === currentId ? { id: currentId, ...values, status: 'DRAFT' } : apt
            ),
        }));
    };

    const handleEdit = (id: string): void => {
        useBookingStore.setState((state) => {
            const appointmentForm = state.appointments_form_draft.find((item) => item.id === id);
            return appointmentForm ? { initial_form_values: appointmentForm } : {};
        });
    };

    const handleDelete = (id: string): void => {
        // Remove draft form from booking store list.
        useBookingStore.setState((state) => {
            const filtered = state.appointments_form_draft.filter((item) => item.id !== id);
            const isCurrentDeleted = state.current_appointment_or_draft_id === id;

            const wasConfiguringCollection = state.is_chain_mode || state.isSeriesMode;
            const collectionResetPatch =
                wasConfiguringCollection && filtered.length === 0
                    ? {
                          is_chain_mode: false,
                          isSeriesMode: false,
                      }
                    : {};

            return {
                appointments_form_draft: filtered,
                current_appointment_or_draft_id: isCurrentDeleted ? undefined : state.current_appointment_or_draft_id,
                is_last_form_valid: isCurrentDeleted ? true : state.is_last_form_valid,
                ...collectionResetPatch,
            };
        });
    };

    const handleCheckIn = (appointment?: Appointment): void => {
        if (!currentPatient) return;

        let dailyAppointment: DailyAppointment | undefined;
        if (appointment) {
            dailyAppointment = {
                ...appointment,
                patient: convertGenericPatientToDailyAppointmentPatient(currentPatient, appointment),
            };
        }

        openPatientModal(
            currentPatient.pvs_user_id,
            currentPatient.eterno_user_id,
            currentPatient.instance_id,
            `${currentPatient.personal.last_name} ${currentPatient.personal.first_name}`,
            dailyAppointment
        );
    };

    if (isCollectionSubmitting && (isSeriesMode || isChainMode)) {
        return (
            <div className="flex min-h-[71vh] flex-col gap-4">
                <div className="flex flex-grow flex-col gap-4">
                    {formOptionData &&
                        orderedBookedItems.map((item) =>
                            item.kind === 'collection' ? (
                                <CollectionSummaryCard
                                    key={`collection-${item.collection.booking_order}`}
                                    mode={item.collection.type}
                                    appointments={item.collection.appointments}
                                    formOptionData={formOptionData}
                                />
                            ) : (
                                <CollapsedAppointment
                                    key={item.appointment.form_id}
                                    disableEdit
                                    disableCancel
                                    appointment={formToAppointment(item.appointment.form_data, formOptionData)}
                                    onEdit={() => {}}
                                    onDelete={() => {}}
                                    batchId={currentBatch?.batch_id ?? ''}
                                    appointmentId={item.appointment.form_id}
                                    onCheckIn={() => handleCheckIn(item.appointment.appointment)}
                                />
                            )
                        )}
                    <div className="flex flex-grow flex-col items-center justify-center gap-3">
                        <CircularLoader />
                        <Paragraph className="text-slate-500">Buchung wird abgeschlossen…</Paragraph>
                    </div>
                    <BookingTaskTiles />
                </div>

                <BookingActionsBar showAddAppointment />
            </div>
        );
    }

    if (isLoading || !formOptionData || !formInitialState) {
        return (
            <div className="flex h-full items-center justify-center">
                <CircularLoader />
            </div>
        );
    }

    const selectedAppointmentType = formOptionData.appointment_types.find(
        (t) => t.id === currentForm?.apt_type_id_or_schedule_type_pvs_id
    );
    const selectedPractitioner = formOptionData.practitioners.find((p) =>
        p.calendars.some((c) => c.pvs.id === currentForm?.calendar_pvs_id)
    );

    const slotDuration = selectedAppointmentType?.duration?.value ?? 30;

    const isShowingSeriesList = isSeriesMode && collectionStep === CollectionStep.LIST;
    const isShowingChainTuner =
        isChainMode && (chainBookingStep === ChainBookingStep.TUNE || chainBookingStep === ChainBookingStep.SUGGEST);

    const isSingleMode = !isSeriesMode && !isChainMode;

    const showActionsBar =
        (isSingleMode || hasBookingContext || !!editingAppointment) && !isShowingSeriesList && !isShowingChainTuner;

    const handleSlotSelect = (slot: string): void => {
        if (!editingId) return;
        const current = useBookingStore.getState().collection_occurrences;
        const idx = current.findIndex((o) => o.id === editingId);
        if (idx === -1) return;
        const start = dayjs(slot);
        const end = start.add(slotDuration, 'minute');
        const updated = current.map((o, i) =>
            i === idx
                ? {
                      ...o,
                      start_datetime: start.toISOString(),
                      end_datetime: end.toISOString(),
                      availability: AvailabilityStatus.AVAILABLE,
                      original_time: undefined,
                  }
                : o
        );
        useBookingStore.setState({ collection_occurrences: updated });
    };

    return (
        <div className="flex min-h-[71vh] flex-col gap-4">
            <AnimatePresence>
                {isShowingSeriesList && (
                    <motion.div key="collection-list" {...EXPAND_COLLAPSE_ANIMATION}>
                        <CollectionList onEditOccurrence={setEditingId} />
                    </motion.div>
                )}
                {isShowingChainTuner && (
                    <motion.div key="chain-tuner" {...EXPAND_COLLAPSE_ANIMATION}>
                        <ChainBookingSlotTuner readOnly={chainBookingStep === ChainBookingStep.SUGGEST} />
                    </motion.div>
                )}
            </AnimatePresence>

            {!isShowingSeriesList && !isShowingChainTuner && (
                <div className="flex flex-grow flex-col gap-4">
                    <AnimatePresence mode="popLayout">
                        <LayoutGroup>
                            {orderedBookedItems.map((item) =>
                                item.kind === 'collection' ? (
                                    <motion.div
                                        key={`collection-${item.collection.booking_order}`}
                                        layoutId={`collection-${item.collection.booking_order}`}
                                        layoutDependency={currentAppointmentOrDraftId}
                                        transition={{ type: 'tween' }}
                                        initial={{ opacity: 0.5 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0.5 }}
                                    >
                                        <CollectionSummaryCard
                                            mode={item.collection.type}
                                            appointments={item.collection.appointments}
                                            formOptionData={formOptionData}
                                        />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key={`collapsed-${item.appointment.form_id}`}
                                        layoutId={item.appointment.form_id}
                                        layoutDependency={currentAppointmentOrDraftId}
                                        transition={{ type: 'tween' }}
                                        initial={{ opacity: 0.5 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0.5 }}
                                    >
                                        <CollapsedAppointment
                                            disableEdit={!isLastFormValid}
                                            disableCancel
                                            appointment={formToAppointment(item.appointment.form_data, formOptionData)}
                                            onEdit={() => {}}
                                            onDelete={() => {}}
                                            batchId={currentBatch?.batch_id ?? ''}
                                            appointmentId={item.appointment.form_id}
                                            onCheckIn={() => handleCheckIn(item.appointment.appointment)}
                                        />
                                    </motion.div>
                                )
                            )}
                            {(!isBookingLoading || isSeriesMode) &&
                                appointmentsFormDraft.map(({ id, ...appointment }, index) =>
                                    id === currentAppointmentOrDraftId ? (
                                        <motion.div
                                            key={`active-${id}`}
                                            layoutId={id}
                                            layoutDependency={currentAppointmentOrDraftId}
                                            style={{ overflow: 'hidden' }}
                                            transition={{ type: 'tween', ease: ['easeInOut'] }}
                                            initial={{ opacity: 0.5 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0.5 }}
                                        >
                                            <AppointmentForm
                                                setIsFormValid={setIsLastFormValid}
                                                setFormData={setCurrentFormData}
                                                initialState={formInitialState}
                                                disableCancel={
                                                    currentBatch?.enriched_appointments.length === 0 &&
                                                    appointmentsFormDraft.length <= 1
                                                }
                                                onDelete={() => {
                                                    handleDelete(id);
                                                    setIsLastFormValid(true);
                                                }}
                                                allowTransition={appointmentsFormDraft.length > 1}
                                                // Easiest way to check if the form is prefilled.
                                                enableSteps={!initialFormValues?.apt_type_id_or_schedule_type_pvs_id}
                                                formOptionData={formOptionData}
                                                isFormOptionDataLoading={isLoading}
                                                // TODO: implement proper feature setup depending on the instance and appointment registered or not
                                                options={{
                                                    enable_priority:
                                                        validateUUID(currentAppointmentOrDraftId) ||
                                                        currentAppointmentOrDraftId.includes('mantine') || // for initial form with random id generated by mantine
                                                        formOptionData.pvs_provider === PVSProvider.DOC_CIRRUS,
                                                    enable_location:
                                                        formOptionData.pvs_provider === PVSProvider.DOC_CIRRUS,
                                                }}
                                            />
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            key={`collapsed-${id}`}
                                            layoutId={id}
                                            layoutDependency={currentAppointmentOrDraftId}
                                            transition={{ type: 'tween', ease: ['easeInOut'] }}
                                            initial={{ opacity: 0.5 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0.5 }}
                                        >
                                            <CollapsedAppointment
                                                disableEdit={!isLastFormValid || isBookingLoading}
                                                disableCancel={
                                                    (currentBatch?.enriched_appointments.length === 0 &&
                                                        appointmentsFormDraft.length <= 1) ||
                                                    isBookingLoading
                                                }
                                                appointment={formToAppointment(appointment, formOptionData)}
                                                appointmentCount={`(${index + 1} of ${appointmentsFormDraft.length})`}
                                                onEdit={() => {
                                                    useBookingStore.setState({ current_appointment_or_draft_id: id });
                                                    handleEdit(id);
                                                }}
                                                onDelete={() => handleDelete(id)}
                                            />
                                        </motion.div>
                                    )
                                )}
                        </LayoutGroup>
                    </AnimatePresence>
                    <BookingTaskTiles />
                </div>
            )}
            {showActionsBar && (
                <BookingActionsBar showAddAppointment={(isSingleMode || hasBookingContext) && !editingAppointment} />
            )}

            {editingOccurrence && selectedPractitioner && currentForm?.calendar_pvs_id && (
                <SlotAdjustmentModal
                    opened
                    onClose={() => setEditingId(undefined)}
                    initialDate={editingOccurrence.start_datetime}
                    selectedAppointmentType={selectedAppointmentType}
                    practitioner={selectedPractitioner}
                    selectedCalendar={currentForm.calendar_pvs_id}
                    insurance={currentForm?.insurance ?? 'PUBLIC'}
                    onSlotSelect={handleSlotSelect}
                    mode="series"
                />
            )}
        </div>
    );
};
